import React, { useState } from "react";
import { AwardsItem } from "..";
import Image from "next/image";
import { toWebp } from "@/utils/common";
import { useRequestAnimationFrame } from "@/hooks/useRequestAnimationFrame";
import { useDebounce } from "@/hooks/useDebounce";

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL;

// How long a card takes to glide to its new position. Tune this to change the
// travel speed; the 4s autoplay interval below controls the pause between moves.
const SLIDE_TRANSITION_CLASS = "duration-1000 ease-out";

interface SliderProps {
  sliderData: AwardsItem[];
}

const Slider = ({ sliderData }: SliderProps) => {
  const total = sliderData.length;
  const [centerIdx, setCenterIdx] = useState(total >= 3 ? 2 : 0);
  const [isPaused, setIsPaused] = useState(false);

  // Debounced handlers to prevent rapid clicks from blocking main thread
  // Must be called before early return (React Hooks rules)
  const handleNext = useDebounce(() => {
    setIsPaused(true);
    setTimeout(() => {
      setCenterIdx((prev) => (prev + 1) % total);
      setIsPaused(false);
    }, 500);
  }, 100);

  const handlePrev = useDebounce(() => {
    setIsPaused(true);
    setTimeout(() => {
      setCenterIdx((prev) => (prev - 1 + total) % total);
      setIsPaused(false);
    }, 500);
  }, 100);

  // Use requestAnimationFrame instead of setInterval for smoother performance
  useRequestAnimationFrame(
    () => setCenterIdx((prev) => (prev + 1) % total),
    4000,
    !isPaused && total >= 3
  );

  // Early return if not enough slides
  if (total < 3) {
    return (
      <div className="flex flex-col items-center">
        <h1>Services Slider</h1>
        <div className="flex gap-4 items-center my-4">
          <div className="flex justify-between items-center gap-0 h-[390px]">
            {sliderData.map((slide) => (
              <div
                key={slide?.id}
                className="flex items-center justify-center rounded w-[292px] h-[292px] bg-green-500 opacity-100"
              >
                <Image
                  src={toWebp(`${baseUrl}${slide?.logo?.data?.attributes?.url}`)}
                  alt={slide?.name}
                  width={262}
                  height={262}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="text-red-500">
          Need at least 3 slides for carousel effect.
        </div>
      </div>
    );
  }

  type Device = "mobile" | "tablet" | "desktop";

  // Each slide keeps its own DOM node and is positioned by how far it sits from
  // the centre, so advancing the carousel only changes each node's transform.
  // The browser then tweens that movement instead of the cards swapping content
  // in place, which is what made the previous version look jerky.
  // sizes/positions are indexed by absolute distance from the centre slide.
  const deviceConfig: Record<
    Device,
    {
      sizes: number[];
      positions: number[];
      maxVisible: number;
      containerHeightClass: string;
      imageSize: (isCenter: boolean) => number;
      dimOpacity: number;
      visibilityClass: string;
      borderClass: string;
    }
  > = {
    mobile: {
      sizes: [300, 252],
      positions: [0, 276],
      maxVisible: 1,
      containerHeightClass: "h-[200px]",
      imageSize: (isCenter) => (isCenter ? 150 : 35),
      dimOpacity: 0.4,
      visibilityClass: "block md:hidden",
      borderClass: "",
    },
    tablet: {
      sizes: [350, 302],
      positions: [0, 326],
      maxVisible: 1,
      containerHeightClass: "h-[390px]",
      imageSize: () => 262,
      dimOpacity: 0.7,
      visibilityClass: "hidden md:block lg:hidden",
      borderClass: "",
    },
    desktop: {
      sizes: [390, 342, 292],
      positions: [0, 366, 683],
      maxVisible: 2,
      containerHeightClass: "h-[390px]",
      imageSize: () => 262,
      dimOpacity: 0.7,
      visibilityClass: "hidden lg:block",
      borderClass: "border-[#B0D9F7]",
    },
  };

  const renderSlides = (device: Device) => {
    const config = deviceConfig[device];
    // One step beyond the visible edge, so a slide is already mounted off-screen
    // and can animate inwards rather than popping into existence.
    const windowRadius = config.maxVisible + 1;
    const clamp = (distance: number, values: number[]) =>
      values[Math.min(distance, values.length - 1)];

    return (
      <div
        // No overflow-hidden here on purpose: the outer wrapper already clips
        // (and goes overflow-visible at xl), which is what lets the outer cards
        // bleed past the container instead of being cut off at its edge.
        className={`${config.visibilityClass} relative w-full ${config.containerHeightClass}`}
      >
        {sliderData.map((slide, i) => {
          // shortest circular distance, so wrapping past the last slide still
          // moves every card by a single step instead of rewinding the track
          let offset = i - centerIdx;
          if (offset > total / 2) offset -= total;
          if (offset < -total / 2) offset += total;

          const distance = Math.abs(offset);
          if (distance > windowRadius) return null;

          const isCenter = offset === 0;
          const isVisible = distance <= config.maxVisible;
          const size = clamp(distance, config.sizes);
          const x = Math.sign(offset) * clamp(distance, config.positions);
          const imageSize = config.imageSize(isCenter);
          // Every card is laid out at the centre size and shrunk with a scale transform. Animating
          // width/height re-laid out and repainted the cards on every frame of each step, which
          // stalled scrolling in Safari; a transform is composited on the GPU instead. The fixed-size
          // mobile logo is enlarged by 1 / scale so it shows at the same size as before.
          const maxSize = config.sizes[0];
          const scale = size / maxSize;

          const bgImageSrc = isCenter
            ? "/images/award-slider-cover.webp"
            : "/images/award-slider-card.webp";

          return (
            <div
              key={slide?.id}
              className={`absolute top-1/2 left-1/2 flex items-center justify-center border transition-[transform,opacity,filter,border-color] ${SLIDE_TRANSITION_CLASS} ${
                isCenter
                  ? "z-20 border-border grayscale-0"
                  : "z-10 border-border-light grayscale"
              } ${config.borderClass}`}
              style={{
                width: maxSize,
                height: maxSize,
                transform: `translate(calc(-50% + ${x}px), -50%) scale(${scale})`,
                opacity: isVisible ? (isCenter ? 1 : config.dimOpacity) : 0,
                pointerEvents: isVisible ? "auto" : "none",
                willChange: "transform, opacity",
              }}
            >
              {/* Background Image (using next/image) */}
              <Image
                src={bgImageSrc}
                alt="Background"
                fill
                style={{ aspectRatio: "1 / 1" }}
                className="object-contain"
                priority={isCenter && device === "mobile"}
                sizes="(max-width: 767px) 320px, (max-width: 1024px) 350px, 390px"
              />

              {/* Foreground Logo (the partner/slide image) */}
              <div className="relative z-10 flex items-center justify-center">
                <Image
                  src={toWebp(`${baseUrl}${slide?.logo?.data?.attributes?.url}`)}
                  alt={slide?.name}
                  quality={100}
                  width={Math.round((imageSize * 2) / scale)}
                  height={Math.round((imageSize * 2) / scale)}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center xl:overflow-visible overflow-hidden pt-10 sm:pt-0">
      <div
        className="flex gap-4 items-center my-4 w-full"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        {renderSlides("mobile")}
        {renderSlides("tablet")}
        {renderSlides("desktop")}
      </div>

      <div className="md:hidden mt-6 flex justify-center gap-2">
        {/* Custom Pagination */}
        <div className="mt-[30px] lg:mt-[15px] flex justify-center items-center gap-2 px-12 py-6 sm:w-[67%] mx-auto relative sm:absolute bottom-[15%] lg:bottom-[20%] left-[50%] sm:transform -translate-x-[50%] z-[5]">
          {sliderData.map((_, idx) => (
            <div
              key={idx}
              onClick={() => setCenterIdx(idx)}
              className={`p-0 relative rounded overflow-hidden ${centerIdx === idx
                ? " flex w-[80px] h-1 bg-[#E6F3FD]"
                : "flex-none w-[16px] h-1 bg-[#E6F3FD]"
                }`}
            >
              <span
                className={`absolute top-0 rounded left-0  h-1 ${centerIdx === idx
                  ? "bg-gradient-to-r from-[#8ABC00] to-[#0086E6] transition-all duration-[4000ms] w-[80px] "
                  : "left-[-100%] w-[16px] "
                  }`}
              ></span>
            </div>
          ))}
        </div>
      </div>

      <div className="hidden md:flex justify-between items-center gap-2 mt-6">
        <button
          onClick={handlePrev}
          className="p-2 border border-border hover:border-text-button transition duration-300 group"
          aria-label="Previous slide"
        >
          <svg
            className="arrow"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M9.57 5.93L3.5 12l6.07 6.07"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-secondary-text group-hover:stroke-text-button"
            />
            <path
              d="M20.5 12H3.67"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-secondary-text group-hover:stroke-text-button"
            />
          </svg>
        </button>
        <span className="text-subtitle-1 font-bold text-secondary-text">
          {centerIdx + 1} / {total}
        </span>
        <button
          onClick={handleNext}
          className="p-2 border border-border hover:border-text-button transition duration-300 group"
          aria-label="Next slide"
        >
          <svg
            className="arrow"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M14.43 5.93L20.5 12l-6.07 6.07"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-secondary-text group-hover:stroke-text-button"
            />
            <path
              d="M3.5 12h16.83"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="stroke-secondary-text group-hover:stroke-text-button"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Slider;
